// Pay on a student's pending booking (ADR 0011). One press starts one booking payment for this
// booking and every other pending booking it may cover, then opens JTPay's pay page. A booking a
// payment already holds reads "Waiting for payment", and Pay opens that same pay page again (JTP-3).
//
// The amount is worked out by the API from stored prices; the button sends the booking id only.
import React from 'react';
import { Badge, Btn } from './index';
import { TestPaymentsNote } from './TestPaymentsNote';
import { api } from '../lib/api';
import { isWaitingForPayment } from '../lib/payments';
import { palette as COLORS, type } from '../design';

const FONT = "'Plus Jakarta Sans', sans-serif";

// Said when the API gives no message of its own, such as a dropped connection (JTP-4).
const START_FAILED_MESSAGE = 'Payment could not start. Try again.';

function openPayPage(url) {
  window.location.assign(url);
}

// The booking's payment state next to Pay: held by a payment, or not yet paid.
export function PayBadge({ booking }) {
  if (!booking || booking.status !== 'pending') return null;
  return isWaitingForPayment(booking)
    ? <Badge variant="blue">Waiting for payment</Badge>
    : <Badge variant="gold">Awaiting payment</Badge>;
}

// `goTo` opens the pay page; tests pass their own, because a browser's address cannot be stubbed.
export function PayButton({ booking, goTo = openPayPage, hint = true }) {
  const hintId = React.useId();
  const [starting, setStarting] = React.useState(false);
  const [error, setError] = React.useState(null);

  if (!booking || booking.status !== 'pending') return null;
  const waiting = isWaitingForPayment(booking);

  const pay = async () => {
    setStarting(true);
    setError(null);
    try {
      const result = await api.post('/booking-payments', { bookingId: booking.id });
      const url = result?.bookingPayment?.checkoutUrl;
      if (!url) throw new Error('No pay page in the answer');
      goTo(url);
      // The page is leaving; the button stays busy so a second press cannot start another.
    } catch (err) {
      setError(err?.data?.message || START_FAILED_MESSAGE);
      setStarting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, maxWidth: 320 }}>
      <Btn size="sm" onClick={pay} disabled={starting} aria-describedby={hint ? hintId : undefined}>
        {starting ? 'Opening…' : 'Pay'}
      </Btn>
      {hint && (
        <span id={hintId} style={{ fontFamily: FONT, fontSize: type.caption, color: COLORS.driftwood, textAlign: 'right' }}>
          {waiting
            ? 'Finish paying on JTPay\'s pay page.'
            : 'One payment covers all your pending bookings.'}
        </span>
      )}
      <TestPaymentsNote align="right" />
      {error && (
        <span role="alert" style={{ fontFamily: FONT, fontSize: type.label, color: COLORS.errorRed, textAlign: 'right', lineHeight: 1.5 }}>{error}</span>
      )}
    </div>
  );
}
